import React, { type ReactNode, useCallback, useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import InviteTeamIconDI from '@atlaskit/icon/core/migration/person-add--invite-team';
import { fireCoreInvitesInJsmProjectFeatureExposed } from '@atlassian/jira-core-invites-in-projects/src/common/utils/analytics/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import InvitePeopleModalAsync from '@atlassian/jira-project-invite-people-modal/async.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';

export type InviteTeamItemNewProps = {
	children: ReactNode;
};
export const InviteTeamItemNew = ({ children }: InviteTeamItemNewProps) => {
	const [{ data: pageContextData, loading }] = usePageContext();
	const { projectContext } = pageContextData || {};
	const { projectId, projectName, projectKey } = projectContext || {};

	const { serviceDesk: jsmEdition } = useAppEditions();
	const isSiteAdmin = useIsSiteAdmin();
	const cloudId = useCloudId();

	const [isInviteTeamDialogOpen, setIsInviteTeamDialogOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onOpenAddPeopleDialog = useCallback((): void => {
		setIsInviteTeamDialogOpen(true);

		fireCoreInvitesInJsmProjectFeatureExposed(
			createAnalyticsEvent,
			expVal('jsm_core_invites_experiment', 'cohort', 'not-enrolled'),
		);
	}, [setIsInviteTeamDialogOpen, createAnalyticsEvent]);

	const onCloseAddPeopleDialog = useCallback((): void => {
		setIsInviteTeamDialogOpen(false);
	}, [setIsInviteTeamDialogOpen]);

	if (loading) return null;

	return (
		projectId && (
			<JSErrorBoundary
				id="jira-navigation-apps-sidebar-servicedesk-core-invites-experience"
				packageName="jira-navigation-apps-sidebar-servicedesk"
				teamName="navigation"
			>
				<MenuItem
					analytics={{ itemId: 'coreInvitesSideNav' }}
					onClick={onOpenAddPeopleDialog}
					iconBefore={<InviteTeamIconDI label="" LEGACY_size="medium" spacing="spacious" />}
				>
					{children}
				</MenuItem>
				{isInviteTeamDialogOpen && (
					<InvitePeopleModalAsync
						appEdition={jsmEdition}
						cloudId={cloudId}
						isOpen={isInviteTeamDialogOpen}
						onClose={onCloseAddPeopleDialog}
						projectId={projectId}
						jiraProjectKey={projectKey}
						jiraProjectName={projectName}
						isSiteAdmin={isSiteAdmin}
					/>
				)}
			</JSErrorBoundary>
		)
	);
};
